import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useValidateInsuranceCredentialsMutation } from 'services/api/businessUnits.api';
import {
  INSURANCE_COMPANIES_MAP,
  type InsuranceBrokerCredential,
  type InsuranceCompany,
} from 'services/types/businessUnit';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

type ValidationStatus = 'idle' | 'validating' | 'success' | 'error';

const VALIDATION_SUPPORTED_COMPANIES: InsuranceCompany[] = ['ZURICH', 'BCI', 'HDI', 'SURA', 'REALE'];

interface InsuranceCredentialsDetailProps {
  selectedCompany: InsuranceCompany | null;
  credentials: InsuranceBrokerCredential[];
  onCredentialsChange: (credentials: InsuranceBrokerCredential[]) => void;
  onSave: (credentialsToSave: InsuranceBrokerCredential[]) => void;
  isSaving: boolean;
  businessUnitId: number;
}

/**
 * Component for editing credentials of a selected insurance company
 *
 * @param selectedCompany - Currently selected company
 * @param credentials - Array of current credentials
 * @param onCredentialsChange - Callback when credentials are modified
 * @param onSave - Callback to save credentials
 * @param isSaving - Whether credentials are currently being saved
 * @param businessUnitId - ID of the business unit
 * @returns React component for editing insurance credentials
 */
const InsuranceCredentialsDetail: React.FC<InsuranceCredentialsDetailProps> = ({
  selectedCompany,
  credentials,
  onCredentialsChange,
  onSave,
  isSaving,
  businessUnitId,
}) => {
  const [localCredentials, setLocalCredentials] = useState<InsuranceBrokerCredential | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>('idle');

  const [validateCredentials, { isLoading: isValidating }] = useValidateInsuranceCredentialsMutation();

  useEffect(() => {
    if (selectedCompany) {
      const credential = credentials.find((c) => c.company === selectedCompany) || null;
      setLocalCredentials(credential);
      setHasChanges(false);
      setValidationStatus('idle');
    } else {
      setLocalCredentials(null);
      setHasChanges(false);
      setValidationStatus('idle');
    }
  }, [selectedCompany, credentials]);

  const updateLocalCredential = (field: 'username' | 'password' | 'discount', value: string | number | null) => {
    if (!selectedCompany) return;

    const newCredential: InsuranceBrokerCredential = {
      company: selectedCompany,
      username: field === 'username' ? (value as string | null) : localCredentials?.username || null,
      password: field === 'password' ? (value as string | null) : localCredentials?.password || null,
      discount: field === 'discount' ? (value as number | null) : (localCredentials?.discount ?? null),
    };

    setLocalCredentials(newCredential);
    setHasChanges(true);
    setValidationStatus('idle');
  };

  const supportsValidation = selectedCompany && VALIDATION_SUPPORTED_COMPANIES.includes(selectedCompany);

  const handleValidate = async () => {
    if (!selectedCompany || !localCredentials?.username || !localCredentials?.password) return;

    setValidationStatus('validating');

    try {
      const result = await validateCredentials({
        businessUnitId,
        company: selectedCompany,
        username: localCredentials.username,
        password: localCredentials.password,
      }).unwrap();

      setValidationStatus(result.success ? 'success' : 'error');
    } catch {
      setValidationStatus('error');
    }
  };

  const handleSave = () => {
    if (!selectedCompany || !localCredentials) return;

    const updatedCredentials = [...credentials];
    const existingIndex = updatedCredentials.findIndex((c) => c.company === selectedCompany);

    if (existingIndex >= 0) {
      updatedCredentials[existingIndex] = localCredentials;
    } else {
      updatedCredentials.push(localCredentials);
    }

    onCredentialsChange(updatedCredentials);
    setHasChanges(false);
    onSave(updatedCredentials);
  };

  const handleRemove = () => {
    if (!selectedCompany) return;

    const updatedCredentials = credentials.filter((c) => c.company !== selectedCompany);

    onCredentialsChange(updatedCredentials);
    setLocalCredentials(null);
    setHasChanges(false);
    setValidationStatus('idle');
    onSave(updatedCredentials);
  };

  const getLogoPath = (company: InsuranceCompany) => {
    const logoName = company
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    return `/logos/insurance/${logoName}.png`;
  };

  if (!selectedCompany) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontFamily: 'Jost', color: NAVY, opacity: 0.4 }}>
          Selecciona una compañía de seguros para configurar sus credenciales
        </Typography>
      </Box>
    );
  }

  const isConfigured = localCredentials && (localCredentials.username || localCredentials.password);
  const canValidate = supportsValidation && localCredentials?.username && localCredentials?.password && hasChanges;
  const canSave = supportsValidation ? validationStatus === 'success' : hasChanges;

  let alertSeverity: 'success' | 'error' | 'info' = 'info';
  if (validationStatus === 'success') alertSeverity = 'success';
  else if (validationStatus === 'error') alertSeverity = 'error';

  let alertIcon: React.ReactNode;
  if (validationStatus === 'validating') alertIcon = <CircularProgress size={20} />;
  else if (validationStatus === 'success') alertIcon = <CheckCircleIcon />;
  else alertIcon = <ErrorIcon />;

  const textFieldSx = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: RADIUS_ITEM,
      fontFamily: 'Jost',
      '& fieldset': { borderColor: 'rgba(21, 24, 58, 0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(21, 24, 58, 0.25)' },
      '&.Mui-focused fieldset': { borderColor: NAVY },
    },
    '& .MuiInputLabel-root': { fontFamily: 'Jost', color: NAVY, opacity: 0.6 },
    '& .MuiFormHelperText-root': { fontFamily: 'Jost' },
  };

  const primaryButtonSx = {
    fontFamily: 'Jost',
    fontWeight: 600,
    borderRadius: RADIUS_COIN,
    background: YELLOW,
    color: NAVY,
    py: 1.2,
    border: 'none',
    boxShadow: '0px 8px 24px rgba(249, 208, 10, 0.3)',
    '&:hover': {
      background: YELLOW,
      boxShadow: '0px 12px 32px rgba(249, 208, 10, 0.4)',
    },
    '&.Mui-disabled': {
      background: SURFACE_CONTAINER_LOW,
      color: 'rgba(21, 24, 58, 0.4)',
      boxShadow: 'none',
    },
  };

  return (
    <Box sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: RADIUS_CARD,
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <img
            src={getLogoPath(selectedCompany)}
            alt={selectedCompany}
            style={{ width: 40, height: 40, objectFit: 'contain' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" component="h2" sx={{ fontFamily: 'Jost', fontWeight: 700, color: NAVY }}>
            {INSURANCE_COMPANIES_MAP[selectedCompany]}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Jost', color: NAVY, opacity: 0.5 }}>
            Configura las credenciales de acceso al portal
          </Typography>
        </Box>
        {isConfigured && (
          <Button
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleRemove}
            sx={{
              ml: 2,
              fontFamily: 'Jost',
              fontWeight: 600,
              color: '#c62828',
              backgroundColor: 'rgba(198, 40, 40, 0.08)',
              borderRadius: RADIUS_COIN,
              px: 2,
              border: 'none',
              '&:hover': { backgroundColor: 'rgba(198, 40, 40, 0.14)' },
            }}
          >
            Eliminar
          </Button>
        )}
      </Box>

      {/* Form */}
      <Box sx={{ flex: 1 }}>
        <TextField
          fullWidth
          label="Usuario"
          size="medium"
          value={localCredentials?.username || ''}
          onChange={(e) => updateLocalCredential('username', e.target.value || null)}
          sx={textFieldSx}
          placeholder="Ingresa el nombre de usuario"
          disabled={isValidating || isSaving}
        />
        <TextField
          fullWidth
          label="Contraseña"
          type="password"
          size="medium"
          value={localCredentials?.password || ''}
          onChange={(e) => updateLocalCredential('password', e.target.value || null)}
          placeholder={
            localCredentials?.password === '****' ? 'Contraseña configurada (****)' : 'Ingresa la contraseña'
          }
          helperText={
            localCredentials?.password === '****'
              ? 'Contraseña ya configurada. Deja en blanco para mantener la actual.'
              : 'Deja en blanco para mantener la contraseña actual'
          }
          sx={textFieldSx}
          disabled={isValidating || isSaving}
        />

        {/* Discount */}
        <Typography
          variant="caption"
          gutterBottom
          sx={{
            fontFamily: 'Jost',
            fontWeight: 600,
            color: NAVY,
            opacity: 0.5,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
          }}
        >
          Descuento
        </Typography>
        <Grid container spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
          <Grid size={'grow'}>
            <Slider
              value={(localCredentials?.discount ?? 0) * 100}
              onChange={(_, value) => updateLocalCredential('discount', value as number)}
              valueLabelDisplay="auto"
              min={0}
              max={100}
              size="small"
              disabled={isValidating || isSaving}
              sx={{
                color: NAVY,
                '& .MuiSlider-thumb': {
                  backgroundColor: '#F9D00A',
                  border: 'none',
                  boxShadow: '0px 4px 12px rgba(21, 24, 58, 0.15)',
                },
                '& .MuiSlider-track': {
                  backgroundColor: YELLOW,
                  border: 'none',
                },
                '& .MuiSlider-rail': { backgroundColor: SURFACE_CONTAINER_LOW },
              }}
            />
          </Grid>
          <Grid>
            <Input
              type="number"
              value={
                localCredentials?.discount && localCredentials.discount > 0
                  ? Math.round(localCredentials.discount * 100)
                  : 0
              }
              size="small"
              onChange={(e) => {
                const v = Number(e.target.value);
                if (!Number.isNaN(v)) {
                  updateLocalCredential('discount', Math.min(100, Math.max(0, v)));
                }
              }}
              inputProps={{ min: 0, max: 100, step: 1 }}
              endAdornment={
                <InputAdornment position="end" sx={{ fontSize: '0.875rem' }}>
                  %
                </InputAdornment>
              }
              sx={{ width: 80, fontFamily: 'Jost', '& input': { fontSize: '0.875rem', fontFamily: 'Jost' } }}
              disabled={isValidating || isSaving}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Validation Status */}
      {validationStatus !== 'idle' && (
        <Alert
          severity={alertSeverity}
          icon={alertIcon}
          sx={{ mb: 2, borderRadius: RADIUS_ITEM, fontFamily: 'Jost', border: 'none' }}
        >
          {validationStatus === 'validating' && 'Validando credenciales...'}
          {validationStatus === 'success' && 'Credenciales validadas correctamente'}
          {validationStatus === 'error' && 'Credenciales invalidas'}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        {hasChanges && validationStatus === 'idle' && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: RADIUS_ITEM, fontFamily: 'Jost', border: 'none' }}>
            Tienes cambios sin guardar
          </Alert>
        )}

        {supportsValidation ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              onClick={() => {
                handleValidate().catch(() => {});
              }}
              disabled={!canValidate || isValidating || isSaving}
              startIcon={isValidating ? <CircularProgress size={16} /> : null}
              sx={{
                fontFamily: 'Jost',
                fontWeight: 600,
                borderRadius: RADIUS_COIN,
                backgroundColor: NAVY,
                color: '#ffffff',
                py: 1.2,
                border: 'none',
                '&:hover': { backgroundColor: NAVY, opacity: 0.9 },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(21, 24, 58, 0.12)',
                  color: 'rgba(21, 24, 58, 0.4)',
                },
              }}
            >
              {isValidating ? 'Validando...' : 'Validar Credenciales'}
            </Button>
            <Button fullWidth onClick={handleSave} disabled={!canSave || isSaving} sx={primaryButtonSx}>
              {isSaving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Box>
        ) : (
          <>
            <Button
              fullWidth
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              size="large"
              sx={{ ...primaryButtonSx, py: 1.5 }}
            >
              {isSaving ? 'Guardando...' : 'Guardar Credenciales'}
            </Button>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: 'block',
                textAlign: 'center',
                fontFamily: 'Jost',
                color: NAVY,
                opacity: 0.4,
              }}
            >
              Validacion automatica no disponible para {INSURANCE_COMPANIES_MAP[selectedCompany]}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default InsuranceCredentialsDetail;
